import React, { useState, useEffect } from 'react';
import './GenieACSPanel.css';

const GenieACSPanel = ({ onClose }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchType, setSearchType] = useState('sn'); // 'sn' o 'clientId'
  const [searchValue, setSearchValue] = useState('');
  const [device, setDevice] = useState(null);
  const [connectionStatus, setConnectionStatus] = useState('checking');

  // Función helper para obtener valores seguros
  const getSafeValue = (value, defaultValue = 'N/A') => {
    if (value === null || value === undefined) return defaultValue;
    if (typeof value === 'object' && value._value !== undefined) return value._value;
    if (typeof value === 'object') return defaultValue;
    return String(value);
  };

  // Verificar conexión al cargar
  useEffect(() => {
    checkConnection();
  }, []);

  const checkConnection = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/genieacs/list-all', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        setConnectionStatus('connected');
      } else {
        setConnectionStatus('error');
      }
    } catch (err) {
      setConnectionStatus('error');
    }
  };

  const handleSearch = async () => {
    if (!searchValue.trim()) {
      setError('Por favor, ingresa un valor para buscar');
      return;
    }

    setLoading(true);
    setError(null);
    setDevice(null);

    try {
      const token = localStorage.getItem('token');
      const endpoint = searchType === 'sn' 
        ? `/api/genieacs/search-by-sn?sn=${encodeURIComponent(searchValue.trim())}`
        : `/api/genieacs/search-by-client-id?clientId=${encodeURIComponent(searchValue.trim())}`;
      
      console.log('🔍 Buscando dispositivo:', endpoint);
      
      const response = await fetch(endpoint, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      console.log('📡 Respuesta recibida, status:', response.status);
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: `Error ${response.status}: ${response.statusText}` }));
        console.error('❌ Error en respuesta:', errorData);
        setError(errorData.error || `Error ${response.status}: ${response.statusText}`);
        setDevice(null);
        setLoading(false);
        return;
      }
      
      const result = await response.json();
      console.log('📊 Resultado:', result);

      if (result.success) {
        console.log('✅ Dispositivo encontrado:', result.device);
        // Validar y limpiar datos del dispositivo antes de establecerlo
        const cleanedDevice = {
          id: result.device?.id || 'N/A',
          smartOLT_SN: result.device?.smartOLT_SN || null, // SN de SmartOLT (ej: HWTC191F3EAE)
          serialNumber: getSafeValue(result.device?.serialNumber), // Serial Number de GenieACS (ej: 48575443191F3EAE)
          productClass: getSafeValue(result.device?.productClass),
          manufacturer: getSafeValue(result.device?.manufacturer),
          online: result.device?.online || false,
          pppoe: result.device?.pppoe || null,
          wifiNetworks: Array.isArray(result.device?.wifiNetworks) ? result.device.wifiNetworks : [],
          connectedDevices: Array.isArray(result.device?.connectedDevices) ? result.device.connectedDevices : []
        };
        setDevice(cleanedDevice);
        setError(null);
      } else {
        console.error('❌ Dispositivo no encontrado:', result.error);
        setError(result.error || 'No se encontró el dispositivo');
        setDevice(null);
      }
    } catch (err) {
      console.error('❌ Error en handleSearch:', err);
      console.error('   Stack:', err.stack);
      setError(`Error al buscar: ${err.message || 'Error desconocido'}`);
      setDevice(null);
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  // Debug: verificar que el componente se renderiza
  console.log('GenieACSPanel renderizado, showGenieACS:', true);

  return (
    <div className="genieacs-panel-overlay" style={{ zIndex: 10000 }}>
      <div className="genieacs-panel">
        <div className="genieacs-panel-header">
          <h2>GenieACS - Buscar ONU</h2>
          <button className="btn-close" onClick={onClose}>✕</button>
        </div>

        <div className="genieacs-panel-content">
          {/* Estado de conexión */}
          <div className="connection-status">
            {connectionStatus === 'checking' && '⏳ Verificando conexión...'}
            {connectionStatus === 'connected' && '✅ Conectado a GenieACS'}
            {connectionStatus === 'error' && '❌ Error de conexión'}
          </div>

          {/* Selector de tipo de búsqueda */}
          <div className="search-type-selector">
            <label>
              <input
                type="radio"
                value="sn"
                checked={searchType === 'sn'}
                onChange={(e) => setSearchType(e.target.value)}
              />
              Buscar por SN (Serial Number)
            </label>
            <label>
              <input
                type="radio"
                value="clientId"
                checked={searchType === 'clientId'}
                onChange={(e) => setSearchType(e.target.value)}
              />
              Buscar por ID de Cliente
            </label>
          </div>

          {/* Campo de búsqueda */}
          <div className="search-container">
            <input
              type="text"
              className="search-input"
              placeholder={searchType === 'sn' ? 'Ingresa el SN de la ONU' : 'Ingresa el ID del cliente'}
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              onKeyPress={handleKeyPress}
              disabled={loading}
            />
            <button
              className="btn-search"
              onClick={handleSearch}
              disabled={loading || !searchValue.trim()}
            >
              {loading ? 'Buscando...' : '🔍 Buscar'}
            </button>
          </div>

          {/* Mensaje de error */}
          {error && (
            <div className="error-message" style={{ 
              padding: '15px', 
              background: '#fee', 
              border: '1px solid #fcc', 
              borderRadius: '4px', 
              color: '#c33',
              marginBottom: '20px',
              wordBreak: 'break-word'
            }}>
              ❌ {error}
              {error.includes('No se encontró') && (
                <div style={{ marginTop: '10px', fontSize: '0.9em', color: '#666' }}>
                  <strong>Sugerencias:</strong>
                  <ul style={{ marginTop: '5px', paddingLeft: '20px' }}>
                    <li>Verifica que el ID del cliente sea correcto</li>
                    <li>El dispositivo puede tardar en registrarse en GenieACS</li>
                    <li>Verifica que TR-069 esté activado en SmartOLT</li>
                    <li>Intenta buscar por SN en lugar de ID de cliente</li>
                  </ul>
                </div>
              )}
            </div>
          )}

          {/* Resultado del dispositivo */}
          {device && (
            <div className="device-info">
              <h3>✅ Dispositivo Encontrado</h3>
              
              <div className="info-section">
                <h4>Información de la ONU</h4>
                {device.smartOLT_SN && (
                  <div className="info-row">
                    <span className="info-label">SN (SmartOLT):</span>
                    <span className="info-value" style={{ fontWeight: 'bold', color: '#667eea' }}>{getSafeValue(device.smartOLT_SN)}</span>
                  </div>
                )}
                <div className="info-row">
                  <span className="info-label">Serial Number:</span>
                  <span className="info-value">{getSafeValue(device.serialNumber)}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Device ID:</span>
                  <span className="info-value">{device.id}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Product Class:</span>
                  <span className="info-value">{getSafeValue(device.productClass)}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Manufacturer:</span>
                  <span className="info-value">{getSafeValue(device.manufacturer)}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Estado:</span>
                  <span className={`info-value ${device.online ? 'online' : 'offline'}`}>
                    {device.online ? '🟢 En línea' : '🔴 Desconectado'}
                  </span>
                </div>
                {device.pppoe && (
                  <div className="info-row">
                    <span className="info-label">PPPoE Usuario:</span>
                    <span className="info-value">{device.pppoe}</span>
                  </div>
                )}
              </div>

              {/* Redes WiFi */}
              {device.wifiNetworks && Array.isArray(device.wifiNetworks) && device.wifiNetworks.length > 0 && (
                <div className="info-section">
                  <h4>Redes WiFi ({device.wifiNetworks.length})</h4>
                  {device.wifiNetworks.map((wifi, idx) => {
                    if (!wifi) return null;
                    return (
                      <div key={idx} className="wifi-network">
                        <div className="info-row">
                          <span className="info-label">SSID:</span>
                          <span className="info-value">{getSafeValue(wifi.ssid)}</span>
                        </div>
                        <div className="info-row">
                          <span className="info-label">Banda:</span>
                          <span className="info-value">{getSafeValue(wifi.band)}</span>
                        </div>
                        {wifi.associations !== null && wifi.associations !== undefined && (
                          <div className="info-row">
                            <span className="info-label">Dispositivos conectados:</span>
                            <span className="info-value">{wifi.associations || 0}</span>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Dispositivos conectados */}
              {device.connectedDevices && Array.isArray(device.connectedDevices) && device.connectedDevices.length > 0 && (
                <div className="info-section">
                  <h4>Dispositivos Conectados ({device.connectedDevices.length})</h4>
                  <div className="connected-devices-list">
                    {device.connectedDevices.map((dev, idx) => {
                      if (!dev) return null;
                      return (
                        <div key={idx} className="connected-device">
                          <div className="info-row">
                            <span className="info-label">Tipo:</span>
                            <span className="info-value">{dev.type === 'wifi' ? 'WiFi' : 'LAN'}</span>
                          </div>
                          {dev.mac && (
                            <div className="info-row">
                              <span className="info-label">MAC:</span>
                              <span className="info-value">{getSafeValue(dev.mac)}</span>
                            </div>
                          )}
                          {dev.ip && (
                            <div className="info-row">
                              <span className="info-label">IP:</span>
                              <span className="info-value">{getSafeValue(dev.ip)}</span>
                            </div>
                          )}
                          {dev.hostname && (
                            <div className="info-row">
                              <span className="info-label">Hostname:</span>
                              <span className="info-value">{getSafeValue(dev.hostname)}</span>
                            </div>
                          )}
                          {dev.rssi !== null && dev.rssi !== undefined && (
                            <div className="info-row">
                              <span className="info-label">RSSI:</span>
                              <span className="info-value">{getSafeValue(dev.rssi)} dBm</span>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default GenieACSPanel;

